import React, {
  useState,
  useContext,
  useCallback,
  forwardRef,
  createContext,
  useEffect,
  ReactNode,
} from 'react'
import { standardRadioStyle } from './styles/StandardRadioButtons.styles'

// Import the RadioSize type from the styles file
type RadioSize = 's' | 'm' | 'l'

interface RadioContextType {
  selectedValue: string | null
  handleChange: (value: string) => void
  group: string
}

const RadioContext = createContext<RadioContextType | undefined>(undefined)

interface RadioProviderProps {
  children: ReactNode
  onChange: (value: string) => void
  initValue: string
  group?: string
}

// RadioProvider now has a single selectedValue
export const RadioProvider: React.FC<RadioProviderProps> = ({
  children,
  onChange,
  initValue,
  group = 'default',
}) => {
  const [selectedValue, setSelectedValue] = useState<string | null>(null)

  // Effect to handle initial value
  useEffect(() => {
    if (initValue) {
      setSelectedValue(initValue)
    }
  }, [initValue]) // Only run when initValue changes

  const handleChange = useCallback(
    (newValue: string) => {
      setSelectedValue(newValue)
      if (onChange) {
        onChange(newValue)
      }
    },
    [onChange],
  )

  const contextValue: RadioContextType = {
    selectedValue,
    handleChange,
    group,
  }

  return (
    <RadioContext.Provider value={contextValue}>
      {children}
    </RadioContext.Provider>
  )
}

interface StandardRadioProps {
  group?: string
  value: string
  size?: RadioSize
}

// StandardRadio just checks against the single selectedValue
export const StandardRadio = forwardRef<HTMLInputElement, StandardRadioProps>(
  ({ group = 'default', value, size = 'l' }, ref) => {
    const context = useContext(RadioContext)

    if (!context) {
      throw new Error('StandardRadio must be used within a RadioProvider')
    }

    if (context.group !== group) {
      throw new Error(
        `Radio with group "${group}" must match its provider's group "${context.group}"`,
      )
    }

    return (
      <input
        ref={ref}
        type="radio"
        checked={context.selectedValue === value}
        onChange={() => context.handleChange(value)}
        css={standardRadioStyle(size)}
      />
    )
  },
)
